import { Component, EventEmitter, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { UrlConfigContainer } from '@admin/core/url-config';
import { UrlConfigService } from '@hrra/core';
import { FieldState } from '@admin/shared/models/field-state';
import { ValidationUtils } from '@admin/shared/validation/validation-runner';
import { IValidator, Validator } from '@admin/shared/validation/validators';
import { UploadInput, UploadOutput } from '@angular-ex/uploader';
import { BannerModel } from '../shared/models/banner-model';
import { BannerStatusEnum } from '../shared/models/banner-status-enum';
import { BannerTypeEnum } from '../shared/models/banner-type-enum';
import { SettingsService } from '../shared/services/settings.service';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from '@admin/core/auth.service';
import { LocalDataStorageService } from '@admin/core/local-data-storage.service';

@Component({
  selector: 'app-ads-edit',
  templateUrl: './ads-edit.component.html',
  styleUrls: ['./ads-edit.component.less'],
  standalone: false,
})
export class AdsEditComponent implements OnInit {
  public bannerType: any;
  public status: any;

  @Input() bannerPositionId: number;
  @Input() targetId: number;
  @Input() isEdit: boolean;

  public selectedFileName: string;
  uploadInput: EventEmitter<UploadInput>;

  @Input() templateData: BannerModel;
  @Input() type: BannerTypeEnum;
  @Input() templateId: number;

  fieldStates: {
    imageFileName: FieldState;
    link: FieldState;
    code: FieldState;
    name: FieldState;
  };

  constructor(
    private translationService: TranslateService,
    private router: Router,
    private settingsService: SettingsService,
    private urlService: UrlConfigService<UrlConfigContainer>,
    private storage: LocalDataStorageService,
  ) {
    this.templateData = new BannerModel();
    this.bannerType = BannerTypeEnum;
    this.status = BannerStatusEnum;
    this.uploadInput = new EventEmitter<UploadInput>();
  }

  ngOnInit() {
    this.initializeFieldsState();
  }

  private navigateToParent() {
    this.router.navigate([
      '/settings/banner-positions',
      this.bannerPositionId,
      'banners',
    ]);
  }

  onSave() {
    this.invokeValidation(this.getValidators(), () => {
      this.onSaveHelper().subscribe((r) => {
        if (r.success) {
          this.navigateToParent();
        }
      });
    });
  }

  onCancel() {
    this.navigateToParent();
  }

  private onSaveHelper() {
    this.templateData.typeId = this.type;
    this.templateData.statusId = this.status.Inactive;
    this.templateData.positionId = this.bannerPositionId;
    this.templateData.targetId = this.targetId;

    if (this.selectedFileName != null) {
      this.templateData.imageFileName = this.selectedFileName;
    }

    if (this.isEdit) {
      return this.settingsService.updateBanner(this.templateData);
    }
    return this.settingsService.createBanner(this.templateData);
  }

  onUploadOutput(output: UploadOutput): void {
    if (output.type === 'allAddedToQueue') {
      const event: UploadInput = {
        type: 'uploadAll',
        url: this.urlService.urlConfig.api.data.settings.banners.uploadTempFile,
        method: 'POST',
        headers: { Authorization: `Bearer ${this.storage.getAccessToken()}` },
      };
      this.uploadInput.emit(event);
    } else if (output.type === 'done' && output.file.responseStatus == 200) {
      console.log('file' + this.selectedFileName);
      this.selectedFileName = output.file.response.fileName;
    }
  }

  private getValidators() {
    let emptyMessage = this.translationService.instant(
      'message.EmptyFieldDetected'
    );

    let data = this.templateData;
    let fields = this.fieldStates;

    if (this.type == BannerTypeEnum.Image) {
      if (this.isEdit) {
        return [
          Validator.forRequired(fields.link, () => data.link, emptyMessage),
          Validator.forRequired(fields.name, () => data.name, emptyMessage),
        ];
      } else {
        return [
          Validator.forRequired(
            fields.imageFileName,
            () => this.selectedFileName,
            emptyMessage
          ),
          Validator.forRequired(fields.link, () => data.link, emptyMessage),
          Validator.forRequired(fields.name, () => data.name, emptyMessage),
        ];
      }
    } else {
      return [
        Validator.forRequired(fields.code, () => data.code, emptyMessage),
        Validator.forRequired(fields.name, () => data.name, emptyMessage),
      ];
    }
  }

  private invokeValidation(validators: IValidator[], onValid?: () => void) {
    ValidationUtils.invokeValidation(
      validators,
      this.getFieldStatesObject(),
      onValid
    );
  }

  private getFieldStatesObject() {
    let result: { [index: string]: FieldState } = {};
    for (let p in this.fieldStates) {
      if (this.fieldStates.hasOwnProperty(p)) {
        result[this.fieldStates[p].fieldId] = this.fieldStates[p];
      }
    }

    return result;
  }

  private initializeFieldsState() {
    this.fieldStates = {
      imageFileName: new FieldState(() => this.selectedFileName),
      link: new FieldState(() => this.templateData.link),
      code: new FieldState(() => this.templateData.code),
      name: new FieldState(() => this.templateData.name),
    };
  }
}
